import type { ExamPaper } from '@/lib/types';

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type PrintOptions = {
  useCleaned?: boolean;
  includeAnswer?: boolean;
};

// 등록한 시험지를 다시 인쇄하고, 기입한 답안지도 함께 출력할 수 있어요.
// 팝업이 막히는 환경에서도 동작하도록 숨김 iframe을 사용해요.
export function printExamPaper(paper: ExamPaper, options: PrintOptions = {}): void {
  const { useCleaned = false, includeAnswer = false } = options;
  const src =
    useCleaned && paper.cleaned_image_url ? paper.cleaned_image_url : paper.image_url;

  // 이미지가 없고 PDF만 있으면 PDF를 새 탭에서 열어 인쇄할 수 있게 해요.
  if (!src && paper.pdf_url) {
    window.open(paper.pdf_url, '_blank');
    return;
  }

  const title = paper.title || '시험지';
  const meta = [paper.school, paper.grade, paper.term, paper.subject]
    .filter(Boolean)
    .join(' · ');
  const answerText = paper.answer ? escapeHtml(paper.answer) : '';

  const imgHtml = src
    ? `<img class="paper" src="${src}" alt="${escapeHtml(title)}" />`
    : '<p class="empty">시험지 이미지가 없습니다.</p>';

  const answerHtml = includeAnswer
    ? `<section class="answer">
         <h2>답안지 (내가 푼 답)</h2>
         ${answerText ? `<pre>${answerText}</pre>` : '<p class="empty">기입한 답안이 없습니다.</p>'}
       </section>`
    : '';

  const html = `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 24px; font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif; color: #111; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 12px; color: #555; margin-bottom: 16px; }
  .paper { width: 100%; max-width: 100%; display: block; border: 1px solid #ddd; }
  .empty { color: #888; font-size: 13px; padding: 20px; text-align: center; border: 1px dashed #ccc; }
  .answer { margin-top: 24px; page-break-before: always; }
  .answer h2 { font-size: 15px; margin: 0 0 12px; padding-bottom: 6px; border-bottom: 2px solid #111; }
  .answer pre { white-space: pre-wrap; font-family: inherit; font-size: 14px; line-height: 1.8; margin: 0; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  ${meta ? `<div class="meta">${escapeHtml(meta)}</div>` : ''}
  ${imgHtml}
  ${answerHtml}
</body>
</html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  const removeIframe = () => {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1500);
  };

  const runPrint = () => {
    const win = iframe.contentWindow;
    if (!win) {
      removeIframe();
      return;
    }
    try {
      win.focus();
      win.print();
    } catch (err) {
      console.error(err);
    }
    removeIframe();
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    removeIframe();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  // 이미지가 있으면 로드가 끝난 뒤에 인쇄해요. (로드 실패/지연에 대비해 최대 3초 대기)
  const waitForImages = (): Promise<void> =>
    new Promise((resolve) => {
      const imgs = Array.from(doc.querySelectorAll('img'));
      if (imgs.length === 0) {
        resolve();
        return;
      }
      let remaining = imgs.length;
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const fallback = window.setTimeout(done, 3000);
      imgs.forEach((img) => {
        const finish = () => {
          remaining -= 1;
          if (remaining <= 0) {
            window.clearTimeout(fallback);
            done();
          }
        };
        if (img.complete) {
          finish();
        } else {
          img.addEventListener('load', finish, { once: true });
          img.addEventListener('error', finish, { once: true });
        }
      });
    });

  waitForImages().then(() => {
    setTimeout(runPrint, 300);
  });
}